import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Box, Typography, TextField, Button, Stack, FormControl, InputLabel, Select, MenuItem,
    Paper, Switch, Divider
} from '@mui/material';
import { setMainHeading } from '../../../../redux/modules/mainHeading/slice';
import { getLabDepartmentRequest } from '../../../../redux/modules/laboratory/labDepartment/slice';
import {
    createLabAnalyzerRequest, resetCreateLabAnalyzer,
    getByIdLabAnalyzerRequest, resetGetByIdLabAnalyzer,
    updateLabAnalyzerRequest, resetUpdateLabAnalyzer,
} from '../../../../redux/modules/laboratory/labAnalyzer/slice';

export default function LabAnalyzerConfig() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const basePath = '/' + location.pathname.split('/')[1];
    const { id } = useParams();
    const isEdit = !!id;

    const { createRes, getByIdRes, updateRes } = useSelector(state => state.labAnalyzer);
    const { getRes: deptRes } = useSelector(state => state.labDepartment);

    const [form, setForm] = useState({
        name: '', manufacturer: '', model: '', serialNumber: '',
        departmentId: '', protocol: 'ASTM_LIS2A2', driverName: '',
        location: '', supportsWorklist: false, supportsAutoResult: true,
        sampleIdIsAccessionSequence: false,
        connectionType: 'tcp',
        // Serial
        serialPort: '', baudRate: 9600, dataBits: 8, stopBits: 1, parity: 'none',
        // TCP
        host: '', port: '',
        // File
        inputDirectory: '', outputDirectory: '', pollingIntervalMs: 5000, filePattern: '*.txt',
    });

    useEffect(() => {
        dispatch(setMainHeading(isEdit ? "Edit Analyzer" : "Add Analyzer"));
        dispatch(getLabDepartmentRequest());
        if (isEdit) dispatch(getByIdLabAnalyzerRequest(id));
        return () => {
            dispatch(resetCreateLabAnalyzer());
            dispatch(resetUpdateLabAnalyzer());
            dispatch(resetGetByIdLabAnalyzer());
        };
    }, [dispatch, id, isEdit]);

    useEffect(() => {
        if (getByIdRes?.success && getByIdRes.data) {
            const d = getByIdRes.data;
            setForm({
                name: d.name || '', manufacturer: d.manufacturer || '', model: d.model || '',
                serialNumber: d.serialNumber || '', departmentId: d.departmentId?._id || d.departmentId || '',
                protocol: d.protocol || 'ASTM_LIS2A2', driverName: d.driverName || '', location: d.location || '',
                supportsWorklist: d.supportsWorklist || false, supportsAutoResult: d.supportsAutoResult ?? true,
                sampleIdIsAccessionSequence: d.sampleIdIsAccessionSequence || false,
                connectionType: d.connectionConfig?.type || 'tcp',
                serialPort: d.connectionConfig?.serialPort || '', baudRate: d.connectionConfig?.baudRate || 9600,
                dataBits: d.connectionConfig?.dataBits || 8, stopBits: d.connectionConfig?.stopBits || 1,
                parity: d.connectionConfig?.parity || 'none',
                host: d.connectionConfig?.host || '', port: d.connectionConfig?.port || '',
                inputDirectory: d.connectionConfig?.inputDirectory || '', outputDirectory: d.connectionConfig?.outputDirectory || '',
                pollingIntervalMs: d.connectionConfig?.pollingIntervalMs || 5000, filePattern: d.connectionConfig?.filePattern || '*.txt',
            });
        }
    }, [getByIdRes]);

    useEffect(() => {
        if (createRes?.success || updateRes?.success) {
            navigate(`${basePath}/laboratory/analyzers`);
        }
    }, [createRes, updateRes, navigate, basePath]);

    const handleChange = (field) => (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const connectionConfig = { type: form.connectionType };
        if (form.connectionType === 'serial') {
            Object.assign(connectionConfig, {
                serialPort: form.serialPort, baudRate: Number(form.baudRate),
                dataBits: Number(form.dataBits), stopBits: Number(form.stopBits), parity: form.parity,
            });
        } else if (form.connectionType === 'tcp') {
            Object.assign(connectionConfig, { host: form.host, port: Number(form.port) });
        } else if (form.connectionType === 'file_polling') {
            Object.assign(connectionConfig, {
                inputDirectory: form.inputDirectory, outputDirectory: form.outputDirectory,
                pollingIntervalMs: Number(form.pollingIntervalMs), filePattern: form.filePattern,
            });
        }

        const payload = {
            name: form.name, manufacturer: form.manufacturer, model: form.model,
            serialNumber: form.serialNumber, departmentId: form.departmentId || undefined,
            protocol: form.protocol, driverName: form.driverName, location: form.location,
            supportsWorklist: form.supportsWorklist, supportsAutoResult: form.supportsAutoResult,
            sampleIdIsAccessionSequence: form.sampleIdIsAccessionSequence,
            connectionConfig,
        };

        if (isEdit) {
            dispatch(updateLabAnalyzerRequest({ _id: id, ...payload }));
        } else {
            dispatch(createLabAnalyzerRequest(payload));
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 700, mt: 4 }}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>General Information</Typography>
                <Stack spacing={2}>
                    <TextField label="Analyzer Name" value={form.name} onChange={handleChange('name')} required fullWidth size="small" />
                    <Stack direction="row" spacing={2}>
                        <TextField label="Manufacturer" value={form.manufacturer} onChange={handleChange('manufacturer')} fullWidth size="small" />
                        <TextField label="Model" value={form.model} onChange={handleChange('model')} fullWidth size="small" />
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <TextField label="Serial Number" value={form.serialNumber} onChange={handleChange('serialNumber')} fullWidth size="small" />
                        <TextField label="Location" value={form.location} onChange={handleChange('location')} fullWidth size="small" />
                    </Stack>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Department</InputLabel>
                        <Select value={form.departmentId} onChange={handleChange('departmentId')} label="Department">
                            <MenuItem value="">None</MenuItem>
                            {deptRes?.data?.map(d => <MenuItem key={d._id} value={d._id}>{d.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Stack>

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Communication Protocol</Typography>
                <Stack spacing={2}>
                    <Stack direction="row" spacing={2}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Protocol</InputLabel>
                            <Select value={form.protocol} onChange={handleChange('protocol')} label="Protocol">
                                <MenuItem value="ASTM_LIS2A2">ASTM LIS2-A2</MenuItem>
                                <MenuItem value="HL7v2">HL7 v2</MenuItem>
                                <MenuItem value="POCT1A">POCT1-A</MenuItem>
                                <MenuItem value="custom">Custom</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField label="Driver Name" value={form.driverName} onChange={handleChange('driverName')} fullWidth size="small" placeholder="e.g. dca_vantage" />
                    </Stack>

                    {/* Behaviour flags as a settings list: bold label + helper text on
                        the left, switch on the right, one per row, evenly divided. */}
                    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
                        {[
                            {
                                key: 'supportsWorklist',
                                label: 'Supports Worklist',
                                help: "The analyzer can query the LIS for a sample's test worklist before it runs.",
                            },
                            {
                                key: 'supportsAutoResult',
                                label: 'Supports Auto-Result',
                                help: 'Results transmit to the LIS automatically as the analyzer completes them.',
                            },
                            {
                                key: 'sampleIdIsAccessionSequence',
                                label: "Sample ID is the accession's trailing 6 (day + sequence)",
                                help: (
                                    <>
                                        Only for benches whose sample-id field is too small for a full accession (e.g. Getein 1100:
                                        6 characters vs 13), where the SOP is to key the accession's last 6 characters off the tube
                                        label — the day-of-month plus the 4-digit sequence (e.g. accession L202607150007 → key
                                        <strong> 150007</strong>). The LIS rebuilds the full accession from those, taking only the
                                        year and month from the run date, so a sample run on a different day than it was accessioned
                                        still matches. <strong>Leave off otherwise</strong> — if techs key a free-form number
                                        instead, it can rebuild into another patient's accession.
                                    </>
                                ),
                            },
                        ].map((f, i) => (
                            <Box
                                key={f.key}
                                sx={{
                                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, p: 2,
                                    ...(i > 0 && { borderTop: 1, borderColor: 'divider' }),
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{f.label}</Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, lineHeight: 1.5 }}>
                                        {f.help}
                                    </Typography>
                                </Box>
                                <Switch checked={!!form[f.key]} onChange={handleChange(f.key)} sx={{ flexShrink: 0 }} />
                            </Box>
                        ))}
                    </Box>
                </Stack>

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Connection Configuration</Typography>
                <Stack spacing={2}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Connection Type</InputLabel>
                        <Select value={form.connectionType} onChange={handleChange('connectionType')} label="Connection Type">
                            <MenuItem value="serial">Serial Port</MenuItem>
                            <MenuItem value="tcp">TCP/IP</MenuItem>
                            <MenuItem value="file_polling">File Polling</MenuItem>
                        </Select>
                    </FormControl>

                    {form.connectionType === 'serial' && (
                        <>
                            <TextField label="Serial Port" value={form.serialPort} onChange={handleChange('serialPort')} fullWidth size="small" placeholder="e.g. COM3 or /dev/ttyUSB0" />
                            <Stack direction="row" spacing={2}>
                                <TextField label="Baud Rate" type="number" value={form.baudRate} onChange={handleChange('baudRate')} fullWidth size="small" />
                                <TextField label="Data Bits" type="number" value={form.dataBits} onChange={handleChange('dataBits')} fullWidth size="small" />
                                <TextField label="Stop Bits" type="number" value={form.stopBits} onChange={handleChange('stopBits')} fullWidth size="small" />
                            </Stack>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Parity</InputLabel>
                                <Select value={form.parity} onChange={handleChange('parity')} label="Parity">
                                    <MenuItem value="none">None</MenuItem>
                                    <MenuItem value="even">Even</MenuItem>
                                    <MenuItem value="odd">Odd</MenuItem>
                                </Select>
                            </FormControl>
                        </>
                    )}

                    {form.connectionType === 'tcp' && (
                        <Stack direction="row" spacing={2}>
                            <TextField label="Host / IP Address" value={form.host} onChange={handleChange('host')} fullWidth size="small" placeholder="e.g. 192.168.1.100" />
                            <TextField label="Port" type="number" value={form.port} onChange={handleChange('port')} fullWidth size="small" placeholder="e.g. 5000" />
                        </Stack>
                    )}

                    {form.connectionType === 'file_polling' && (
                        <>
                            <Stack direction="row" spacing={2}>
                                <TextField label="Input Directory" value={form.inputDirectory} onChange={handleChange('inputDirectory')} fullWidth size="small" />
                                <TextField label="Output Directory" value={form.outputDirectory} onChange={handleChange('outputDirectory')} fullWidth size="small" />
                            </Stack>
                            <Stack direction="row" spacing={2}>
                                <TextField label="Polling Interval (ms)" type="number" value={form.pollingIntervalMs} onChange={handleChange('pollingIntervalMs')} fullWidth size="small" />
                                <TextField label="File Pattern" value={form.filePattern} onChange={handleChange('filePattern')} fullWidth size="small" />
                            </Stack>
                        </>
                    )}
                </Stack>
            </Paper>

            <Stack direction="row" spacing={2} justifyContent="flex-end" mt={3}>
                <Button variant="outlined" onClick={() => navigate(`${basePath}/laboratory/analyzers`)}>Cancel</Button>
                <Button variant="contained" type="submit">{isEdit ? 'Update Analyzer' : 'Create Analyzer'}</Button>
            </Stack>
        </Box>
    );
}
